import { store } from "../../store";
import { setSessionExpired } from "../../store/redux_auth/authSlice";
import { ApiClient } from "../../api/ApiClient";

/**
 * Centralized handler for all authentication failures (401 errors)
 *
 * This handles:
 * - Component API calls (with navigate)
 * - Socket connection errors (without navigate)
 * - Redux thunks (without navigate)
 * - ApiClient errors (without navigate)
 *
 *
 * @example
 * // In React component:
 * if (response.status === 401) {
 *   handleUnauthorized();
 * }
 *
 * @example
 * // In socket service or Redux thunk:
 * if (error.status === 401) {
 *   handleUnauthorized();
 * }
 */
export function handleUnauthorized() {
  const currentState = store.getState();

  // 1. Safety Check: If the modal is already showing, don't do anything.
  // This prevents multiple 401s from cluttering the dispatch log.
  if (currentState.auth.showSessionExpiredModal) {
    return;
  }

  // 2. Clear CSRF token cache so the next login attempt is fresh
  ApiClient.setCsrfToken(null);

  // 3. Trigger the Modal via Redux
  // We dispatch the new action we created in your authSlice
  store.dispatch(setSessionExpired(true));
}

/**
 * Check if an error is an authentication error (401)
 *
 * @param {Error|Object} error - The error to check
 * @returns {boolean} True if it's a 401 authentication error
 *
 * @example
 * socket.onerror = (err) => {
 *   if (isAuthError(err)) {
 *     handleUnauthorized();
 *   }
 * }
 */
export const isAuthError = (error) => {
  if (error?.status === 401) return true;
  if (error?.response?.status === 401) return true;
  if (error?.code === 401) return true;
  if (error?.message?.includes("401")) return true;
  if (error?.message?.includes("Unauthorized")) return true;

  return false;
};

// Export aliases for backward compatibility
// These allow existing code to work without changes
export const Handler401 = handleUnauthorized;
export const handleAuthError = handleUnauthorized;
